import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';
import { AiSalesSubnav, EmptyState, ErrorState, KpiGrid, SalesFunnel, SkeletonBlock, SourceBanner } from '../components/ui';
import {
  fetchAgent, fetchAnalytics, fetchFollowUps, fetchKnowledge, fetchLead, fetchLeads,
  removeKnowledge, saveKnowledge, updateAgent, updateFollowUp,
} from '../lib/repository';
import { trackAiSalesEvent } from '../lib/telemetry';
import type { FollowUp, KnowledgeCategory, KnowledgeItem, SalesAgentBundle, SalesLead, SalesOverview } from '../types/contracts';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AdminLayout>
      <AiSalesSubnav />
      <SourceBanner />
      {children}
    </AdminLayout>
  );
}

export function AiSalesLeadsPage() {
  const [items, setItems] = useState<SalesLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    const r = await fetchLeads({ search: q || undefined });
    setLoading(false);
    if (!r.ok) { setError(r.message); return; }
    setItems(r.data.items);
  };
  useEffect(() => { void load(); }, [q]);
  return (
    <Shell>
      <div className="flex justify-between mb-4">
        <h1 className="text-xl font-black">Leads</h1>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business, phone" className="border border-gray-200 rounded-lg px-3 py-2 text-xs w-64" />
      </div>
      {loading ? <SkeletonBlock /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState title="No leads yet." body="Your AI Sales Agent will automatically create leads from qualified conversations." />
      ) : null}
      {!loading && items.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
          <table className="min-w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase bg-gray-50">
              <tr>{['Lead','Business','Channel','Stage','Score','Plan','Last activity','Next follow-up','Owner','Status'].map((h) => <th key={h} className="px-3 py-2 text-start">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-gray-50">
                  <td className="px-3 py-2"><Link className="font-semibold text-teal-700" to={`/ai-sales/leads/${l.id}`}>{l.name}</Link></td>
                  <td className="px-3 py-2">{l.business || '—'}</td>
                  <td className="px-3 py-2">{l.channel || '—'}</td>
                  <td className="px-3 py-2">{l.stage}</td>
                  <td className="px-3 py-2 font-bold">{l.score}</td>
                  <td className="px-3 py-2">{l.interested_plan || '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.last_activity_at || '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.next_follow_up_at || '—'}</td>
                  <td className="px-3 py-2">{l.owner || '—'}</td>
                  <td className="px-3 py-2">{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Shell>
  );
}

export function AiSalesLeadDetailPage() {
  const { id } = useParams();
  const [lead, setLead] = useState<SalesLead | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!id) return;
    void (async () => {
      const r = await fetchLead(id);
      if (!r.ok) { setError(r.message); return; }
      setLead(r.data);
      trackAiSalesEvent('lead_opened', { id });
    })();
  }, [id]);
  if (error) return <Shell><ErrorState message={error} onRetry={() => window.location.reload()} /></Shell>;
  if (!lead) return <Shell><SkeletonBlock /></Shell>;
  return (
    <Shell>
      <h1 className="text-xl font-black mb-4">{lead.name}</h1>
      <div className="grid md:grid-cols-3 gap-4">
        <section className="rounded-2xl bg-white border border-gray-100 p-4 text-sm space-y-1">
          <h2 className="font-bold mb-2">Profile</h2>
          <p>{lead.business}</p><p>{lead.phone}</p><p>{lead.email}</p><p>{lead.country}</p>
        </section>
        <section className="rounded-2xl bg-white border border-gray-100 p-4 text-sm space-y-1">
          <h2 className="font-bold mb-2">Business</h2>
          <p>Branches: {lead.branch_count ?? '—'}</p>
          <p>Employees: {lead.user_count ?? '—'}</p>
          <p>System: {lead.current_software || '—'}</p>
        </section>
        <section className="rounded-2xl bg-white border border-gray-100 p-4 text-sm space-y-1">
          <h2 className="font-bold mb-2">Sales</h2>
          <p>Plan: {lead.interested_plan || '—'}</p>
          <p>Score: {lead.score}</p>
          <p>Stage: {lead.stage}</p>
          <p>Intent: {lead.intent || '—'}</p>
          <p>Pain: {lead.pain_point || '—'}</p>
          <p>Objection: {lead.objection || '—'}</p>
        </section>
      </div>
      <section className="mt-4 rounded-2xl bg-white border border-gray-100 p-4">
        <h2 className="font-bold mb-2">Activity</h2>
        {(lead.timeline ?? []).length === 0 ? <p className="text-sm text-gray-400">No activity yet.</p> : (
          <ul className="space-y-2 text-sm">{lead.timeline?.map((e) => <li key={e.id}><span className="text-xs text-gray-400">{e.at}</span> — {e.body}</li>)}</ul>
        )}
      </section>
    </Shell>
  );
}

export function AiSalesFollowUpsPage() {
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const [bucket, setBucket] = useState('today');
  const [items, setItems] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    const r = await fetchFollowUps(bucket);
    setLoading(false);
    if (!r.ok) { setError(r.message); return; }
    setItems(r.data.items);
  };
  useEffect(() => { void load(); }, [bucket]);
  return (
    <Shell>
      <h1 className="text-xl font-black mb-3">Follow-ups</h1>
      <div className="flex gap-1 mb-4 flex-wrap">
        {['today','upcoming','overdue','completed','cancelled','all'].map((b) => (
          <button key={b} type="button" onClick={() => setBucket(b)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${bucket===b?'bg-gray-900 text-white':'bg-gray-100'}`}>{b}</button>
        ))}
      </div>
      {loading ? <SkeletonBlock /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && items.length === 0 ? <EmptyState title="You're all caught up." body="No follow-ups are currently scheduled." /> : null}
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="rounded-xl bg-white border border-gray-100 p-4 text-sm">
            <div className="flex justify-between gap-2">
              <div>
                <p className="font-bold">{f.lead_name}</p>
                <p className="text-xs text-gray-500">{f.reason}</p>
                <p className="text-xs text-gray-400">{f.scheduled_at} · {f.channel}</p>
              </div>
              <div className="flex gap-2">
                <button type="button" className="text-xs font-bold text-teal-700" onClick={() => { trackAiSalesEvent('followup_opened', { id: f.id }); navigate('/ai-sales/inbox'); }}>View</button>
                {can('ai_sales.manage') ? (
                  <>
                    <button type="button" className="text-xs" onClick={() => void updateFollowUp(f.id, { status: 'completed' }).then(() => load())}>Complete</button>
                    <button type="button" className="text-xs text-rose-600" onClick={() => void updateFollowUp(f.id, { status: 'cancelled' }).then(() => load())}>Cancel</button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function AiSalesAgentPage() {
  const { can } = usePlatformPermissions();
  const [agent, setAgent] = useState<SalesAgentBundle | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await fetchAgent();
      if (!r.ok) { setError(r.message); return; }
      setAgent(r.data);
    })();
  }, []);
  if (error) return <Shell><ErrorState message={error} onRetry={() => window.location.reload()} /></Shell>;
  if (!agent) return <Shell><SkeletonBlock /></Shell>;
  const save = () => {
    void updateAgent(agent).then(() => trackAiSalesEvent('agent_settings_updated', {}));
  };
  return (
    <Shell>
      <div className="flex justify-between mb-4">
        <h1 className="text-xl font-black">Agent</h1>
        {can('ai_sales.manage') ? <button type="button" onClick={save} className="px-3 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg">Save</button> : null}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <section className="rounded-2xl bg-white border border-gray-100 p-4 space-y-2">
          <h2 className="font-bold">Identity</h2>
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={agent.name} onChange={(e) => setAgent({ ...agent, name: e.target.value })} />
          <select className="w-full border rounded-lg px-3 py-2 text-sm" value={agent.default_language} onChange={(e) => setAgent({ ...agent, default_language: e.target.value })}>
            <option value="ar">Arabic</option><option value="en">English</option>
          </select>
        </section>
        <section className="rounded-2xl bg-white border border-gray-100 p-4 space-y-3">
          <h2 className="font-bold">Personality</h2>
          {Object.entries(agent.personality).map(([k, v]) => (
            <label key={k} className="block text-xs">
              {k} ({v})
              <input type="range" min={0} max={100} value={v} className="w-full" onChange={(e) => setAgent({ ...agent, personality: { ...agent.personality, [k]: Number(e.target.value) } })} />
            </label>
          ))}
        </section>
        <section className="rounded-2xl bg-white border border-gray-100 p-4">
          <h2 className="font-bold mb-2">Sales behavior</h2>
          {Object.entries(agent.sales_behavior).map(([k, v]) => (
            <p key={k} className="text-xs py-1"><span className="text-gray-400">{k}</span> · {v}</p>
          ))}
        </section>
        <section className="rounded-2xl bg-white border border-gray-100 p-4">
          <h2 className="font-bold mb-2">Business rules (live subscription)</h2>
          <p className="text-xs text-gray-500 mb-2">Pricing source: {agent.business_rules.pricing_source}</p>
          <ul className="text-sm space-y-1">
            {agent.business_rules.plans.map((p) => (
              <li key={p.slug}>{p.name} — {p.price == null ? '—' : `${p.price} ${p.currency ?? ''}`} / {p.billing_period || '—'}</li>
            ))}
          </ul>
        </section>
      </div>
    </Shell>
  );
}

const CATS: KnowledgeCategory[] = ['product', 'pricing', 'sales', 'policies', 'company'];

export function AiSalesKnowledgePage() {
  const { can } = usePlatformPermissions();
  const [cat, setCat] = useState<KnowledgeCategory>('product');
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const load = async () => {
    const r = await fetchKnowledge(cat);
    if (r.ok) setItems(r.data.items);
  };
  useEffect(() => { void load(); }, [cat]);
  return (
    <Shell>
      <h1 className="text-xl font-black mb-3">Knowledge</h1>
      <div className="flex gap-1 mb-4 flex-wrap">
        {CATS.map((c) => <button key={c} type="button" onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${cat===c?'bg-gray-900 text-white':'bg-gray-100'}`}>{c}</button>)}
      </div>
      {items.length === 0 ? <EmptyState title="Your sales knowledge base is empty." body="Add structured records. Pricing still comes from the subscription catalog." /> : (
        <div className="space-y-2 mb-6">
          {items.map((k) => (
            <article key={k.id} className="rounded-xl bg-white border border-gray-100 p-4">
              <div className="flex justify-between">
                <h3 className="font-bold text-sm">{k.title}</h3>
                <span className="text-[10px] uppercase text-gray-400">{k.source} · {k.status}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{k.content}</p>
              {can('ai_sales.manage') && k.source === 'manual' ? (
                <button type="button" className="text-xs text-rose-600 mt-2" onClick={() => void removeKnowledge(k.id).then(() => load())}>Delete</button>
              ) : null}
            </article>
          ))}
        </div>
      )}
      {can('ai_sales.manage') ? (
        <form className="rounded-2xl bg-white border border-gray-100 p-4 space-y-2" onSubmit={(e) => { e.preventDefault(); void saveKnowledge({ title, content, category: cat, status: 'published' }).then(() => { setTitle(''); setContent(''); trackAiSalesEvent('knowledge_item_updated', {}); void load(); }); }}>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <textarea required value={content} onChange={(e) => setContent(e.target.value)} placeholder="Content" className="w-full border rounded-lg px-3 py-2 text-sm" rows={4} />
          <button type="submit" className="px-3 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg">Add record</button>
        </form>
      ) : null}
    </Shell>
  );
}

export function AiSalesAnalyticsPage() {
  const [data, setData] = useState<SalesOverview | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await fetchAnalytics();
      if (!r.ok) { setError(r.message); return; }
      setData(r.data);
    })();
  }, []);
  return (
    <Shell>
      <h1 className="text-xl font-black mb-4">Analytics</h1>
      {error ? <ErrorState message={error} onRetry={() => window.location.reload()} /> : null}
      {!data && !error ? <SkeletonBlock /> : null}
      {data ? <div className="space-y-4"><KpiGrid items={data.kpis} /><SalesFunnel stages={data.funnel} /></div> : null}
    </Shell>
  );
}
